/**
 * Codex Design System — Elevation & Shadows
 *
 * Five elevation levels × two modes (Figma Foundations §7).
 * Dark mode uses deeper, more diffuse shadows + progressively lighter
 * surface colors (defined in semantic/dark.ts).
 *
 * Component elevation reference:
 *   sm  — buttons resting, app bar, cards (resting)
 *   md  — cards (hover), contained buttons (hover), tooltips, FAB resting
 *   lg  — dropdowns, menus, popovers, toasts, FAB hover
 *   xl  — modals, dialogs, drawers, bottom sheets, FAB active
 */

import type { ThemeOptions } from '@mui/material/styles';

// ---------------------------------------------------------------------------
// ShadowSet interface — string values so both light and dark sets satisfy it.
// Using `typeof shadowLight` would infer exact literal strings and reject
// shadowDark (which has different rgba values) as incompatible.
// ---------------------------------------------------------------------------

export interface ShadowSet {
  none: string;
  sm: string;
  md: string;
  lg: string;
  xl: string;
}

export type ShadowLevel = keyof ShadowSet;

// ---------------------------------------------------------------------------
// Named shadow sets
// ---------------------------------------------------------------------------

export const shadowLight: ShadowSet = {
  none: 'none',
  sm: '0px 1px 2px 0px rgba(0,0,0,0.05)',
  md: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -2px rgba(0,0,0,0.1)',
  lg: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
  xl: '0px 20px 25px -5px rgba(0,0,0,0.1), 0px 8px 10px -6px rgba(0,0,0,0.1)',
};

export const shadowDark: ShadowSet = {
  none: 'none',
  sm: '0px 1px 2px 0px rgba(0,0,0,0.3)',
  md: '0px 4px 6px -1px rgba(0,0,0,0.4), 0px 2px 4px -2px rgba(0,0,0,0.3)',
  lg: '0px 10px 15px -3px rgba(0,0,0,0.5), 0px 4px 6px -4px rgba(0,0,0,0.4)',
  xl: '0px 20px 25px -5px rgba(0,0,0,0.6), 0px 8px 10px -6px rgba(0,0,0,0.4)',
};

// ---------------------------------------------------------------------------
// MUI shadow array builder
// MUI expects exactly 25 shadows (indices 0–24).
// We distribute our 4 named levels across all 25 slots.
// ---------------------------------------------------------------------------

export function buildMuiShadows(set: ShadowSet): ThemeOptions['shadows'] {
  return [
    set.none, // 0  — flat
    set.sm, // 1  — sm: buttons resting, app bar
    set.sm, // 2
    set.sm, // 3
    set.md, // 4  — md: cards, hover
    set.md, // 5
    set.md, // 6
    set.md, // 7
    set.lg, // 8  — lg: dropdowns, menus, popovers
    set.lg, // 9
    set.lg, // 10
    set.lg, // 11
    set.lg, // 12
    set.lg, // 13
    set.lg, // 14
    set.lg, // 15
    set.xl, // 16 — xl: modals, dialogs, drawers
    set.xl, // 17
    set.xl, // 18
    set.xl, // 19
    set.xl, // 20
    set.xl, // 21
    set.xl, // 22
    set.xl, // 23
    set.xl, // 24
  ] as unknown as ThemeOptions['shadows'];
}
